import { HintTooltip } from "./HintTooltip";
import { ThresholdBar, type ThresholdStatus } from "./ThresholdBar";

interface WebVitalTileProps {
  label: string;
  value: number | null;
  unit?: string;
  fractionDigits?: number;
  status: ThresholdStatus;
  markerPct: number | null;
  hint?: string;
  isLoading?: boolean;
  errorTitle?: string;
}

const STATUS_VALUE_CLASS: Record<ThresholdStatus, string> = {
  good: "text-success-text-strong",
  warn: "text-warning-text-strong",
  bad: "text-danger-text-strong",
  unknown: "text-neutral-text-strong",
};

const STATUS_PILL: Record<ThresholdStatus, string> = {
  good: "bg-success/15 text-success-text ring-success/30",
  warn: "bg-warning/15 text-warning-text ring-warning/30",
  bad: "bg-danger/15 text-danger-text ring-danger/30",
  unknown:
    "bg-pill-neutral-bg/40 text-neutral-text ring-pill-neutral-ring/40",
};

const STATUS_LABEL: Record<ThresholdStatus, string> = {
  good: "good · p75",
  warn: "needs work · p75",
  bad: "poor · p75",
  unknown: "p75",
};

export function WebVitalTile({
  label,
  value,
  unit = "",
  fractionDigits = 0,
  status,
  markerPct,
  hint,
  isLoading = false,
  errorTitle,
}: WebVitalTileProps) {
  const isErrored = Boolean(errorTitle);
  const effectiveStatus: ThresholdStatus = isErrored ? "bad" : status;
  const formatted =
    value === null || !Number.isFinite(value)
      ? "—"
      : value.toFixed(fractionDigits);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-inset/60 px-4 py-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-fg-muted">
            <span className="truncate">{label}</span>
            {hint && <HintTooltip text={hint} />}
          </div>
          <div
            className={`mt-1 text-3xl font-semibold tabular-nums ${STATUS_VALUE_CLASS[effectiveStatus]}`}
          >
            {isLoading ? (
              <span className="inline-block h-8 w-20 animate-pulse rounded bg-surface-skeleton" />
            ) : (
              <>
                {formatted}
                {unit && (
                  <span className="ml-1 text-base font-normal text-fg-faint">
                    {unit}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${STATUS_PILL[effectiveStatus]}`}
        >
          {STATUS_LABEL[effectiveStatus]}
        </span>
      </div>
      <ThresholdBar status={effectiveStatus} markerPct={markerPct} />
      {isErrored && (
        <div className="text-[11px] text-danger-text">{errorTitle}</div>
      )}
    </div>
  );
}
